import React, {
  useContext, useMemo, useState, useRef, useEffect, forwardRef
} from 'react';
import PropTypes from 'prop-types';
import {
  customType,
  params,
  useDataModel,
  arrayOf,
  string,
  shape,
  number,
  extend
} from '@thd-nucleus/data-sources';
import {
  Button,
  IconButton,
  Drawer,
  DrawerHeader,
  DrawerBody, DrawerFooter, TextField
} from '@one-thd/sui-atomic-components';
import { Chat } from '@one-thd/sui-icons';
import { ProductListContext } from '../ProductListContext';
import { getProductsFromGroups, returnObject } from '../productListHelper';

export const NoteButton = forwardRef(({ itemId, iconSize }, ref) => {
  const [open, setOpen] = useState(false);
  const {
    productsDisplayed,
    list,
    onEditSuccess
  } = useContext(ProductListContext);
  const products = useMemo(() => getProductsFromGroups(list), [list]);
  const { id } = list;
  const currentListItem = (products).find((product) => product.itemId === itemId);
  let currentProduct = (productsDisplayed || []).find((product) => product.itemId === itemId);
  const [comments, setComments] = useState(currentListItem?.comments || '');
  const [noteError, setNoteError] = useState('');
  const listNoteFormRef = useRef();

  const [disableSave, setDisableSave] = useState(true);
  const disableSaveButton = () => setDisableSave(true);
  const enableSaveButton = () => setDisableSave(false);
  const INVALID_NOTE_MESSAGE = 'Invalid Note: Only a-z, 0-9, ! @ # $ % ^ & * ( ) - _ + , . ? " \' : ; \\ are allowed.';

  const handleOpen = () => {
    // Trigger analytics for make note overlay
    global.LIFE_CYCLE_EVENT_BUS.trigger('product-list.make-note-overlay', {});
    setOpen(true);
  };
  const handleClose = (resetValues = true) => {
    if (resetValues) {
      setComments(currentListItem?.comments || '');
      setNoteError('');
      disableSaveButton();
    }
    setOpen(false);
  };

  const [listOperations, listOperationsResponse] = useDataModel('listOperations', {
    variables: {
      id
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    lazy: true
  });

  useEffect(() => {
    const idFromResponse = listOperationsResponse?.data?.listOperations?.id;
    const errorFromResponse = listOperationsResponse?.error;
    if (!listOperationsResponse.loading && (idFromResponse || errorFromResponse)) {
      if (listOperationsResponse?.data?.listOperations?.id) {
        // Trigger analytics for note saved
        global.LIFE_CYCLE_EVENT_BUS.trigger('product-list.note-saved', {});
        handleClose(false);
        onEditSuccess({});
      } else {
        const gqlError = errorFromResponse?.graphQLErrors?.[0];
        if (gqlError) {
          setNoteError(gqlError.message);
        }
      }
    }
  }, [listOperationsResponse.loading]);

  const validateNote = (note, deleteComment) => {
    const validCharacters = /^[ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]+$/;
    let isValid = validCharacters.test(note);
    const blank = !(note || '').replace(/\s/g, '');
    if (note.length > 256) {
      setNoteError('Maximum length of note is 256');
      disableSaveButton();
      return false;
    }
    if (!isValid) {
      if (blank) {
        enableSaveButton();
        return true;
      }
      setNoteError(INVALID_NOTE_MESSAGE);
      disableSaveButton();
      return false;

    }
    if (note === currentListItem?.comments && !deleteComment) {
      disableSaveButton();
      return false;
    }
    enableSaveButton();
    return true;
  };

  const save = async ({ deleteComment = false }) => {
    if (validateNote(comments, deleteComment)) {
      const comment = comments.replace(/[\u2026]/g, '...');

      // Find the current item's group
      const currentGroup = list.groups?.find((group) => group.items?.some((item) => item.itemId === itemId)
      );

      if (currentGroup) {
        // Create updated items array with the new comment
        const updatedItems = currentGroup.items.map((item) => {
          if (item.itemId === itemId) {
            return {
              itemId: item.itemId,
              quantity: item.quantity,
              itemOrder: item.itemOrder,
              comments: deleteComment ? '' : comment
            };
          }
          return {
            itemId: item.itemId,
            quantity: item.quantity,
            itemOrder: item.itemOrder,
            comments: item.comments || ''
          };
        });

        // Create groups array with updated group
        const groupsWithItems = [{
          groupId: currentGroup.groupId,
          products: updatedItems
        }];

        await listOperations({
          variables: {
            operationName: 'UpdateItems',
            id: list.id,
            groups: groupsWithItems
          }
        });
      }
    }
  };

  const onChange = (evt) => {
    const comment = evt?.target?.value;
    setNoteError('');
    setComments(comment);
    validateNote(comment);
  };

  const deleteNote = async () => {
    setComments('');
    setNoteError('');
    await save({ deleteComment: true });
  };

  const image = currentProduct?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  return (
    <>
      <IconButton
        ref={ref}
        icon={Chat}
        iconSize={iconSize}
        onClick={handleOpen}
        aria-label="Add Note"
      />
      {
        open && (
          <Drawer
            open={open}
            onClose={handleClose}
            fixed
          >
            <DrawerHeader
              title="Make a Note"
              onClose={handleClose}
            />
            <DrawerBody>
              <div data-testid={`note-drawer-${itemId}`}>
                <img
                  src={image?.url.replace('<SIZE>', '145')}
                  alt={currentProduct?.identifiers?.productLabel}
                  className="sui-mx-auto sui-aspect-square sui-my-0 sui-max-h-52"
                  width="145px"
                  height="145px"
                />
                <TextField
                  multiline
                  fullWidth
                  minRows={10}
                  value={comments}
                  onChange={onChange}
                  status={noteError ? 'error' : null}
                  statusMessage={noteError}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'note-input'
                    },
                  }}
                />
              </div>
            </DrawerBody>
            <DrawerFooter>
              <Button
                variant="primary"
                data-testid={`note-submit-${itemId}`}
                disabled={disableSave}
                onClick={save}
                fullWidth
              >
                Save
              </Button>
              <div className="sui-my-2">
                <Button variant="secondary" data-testid={`note-delete-${itemId}`} onClick={deleteNote} fullWidth>
                  Delete
                </Button>
              </div>
            </DrawerFooter>
          </Drawer>
        )
      }
    </>
  );
});

NoteButton.displayName = 'ProductListNoteButton';

NoteButton.dataModel = extend({
  listOperations: params({
    operationName: customType('Operation').enum(['UpdateItems', 'Create'], 'UpdateItems'),
    id: string().isRequired(),
    groups: arrayOf(customType('GroupInput').shape({
      groupId: string(),
      groupName: string(),
      groupOrder: string(),
      products: arrayOf(customType('ProductInput').shape({
        itemId: string(),
        quantity: number(),
        itemOrder: number(),
        comments: string()
      }))
    })),
    name: string(),
    listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
  }).mutation().shape({
    id: string(),
    groups: arrayOf(customType('Group').shape({
      groupId: string(),
      status: string(),
      items: arrayOf(customType('Item').shape({
        itemId: string(),
        quantity: number(),
        itemOrder: number(),
        comments: string(),
        status: string()
      }))
    }))
  }),
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      brandName: string(),
      productLabel: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    })
  })
});

NoteButton.propTypes = {
  itemId: PropTypes.string.isRequired,
  iconSize: PropTypes.string
};

NoteButton.defaultProps = {
  iconSize: 'small'
};